import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ICenterCLCDetailsResponse } from '@nkr/store/index';
import { IClcScoresDetails } from '@nkr/models/index';
import { TableWrapper } from './styles';
import {
    IHeaderParams, GridApi, ColumnApi, GridReadyEvent,
    CsvExportParams, ValueGetterParams, ProcessCellForExportParams, ICellRendererParams, ColSpanParams, CellClassParams
} from 'ag-grid-community';
import { AgGridColumnProps, AgGridColumnGroupProps } from 'ag-grid-react';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Alert, Col, FormControl, Row } from 'react-bootstrap';
import ClcDetailsGrid from './grid';

type MyColumn = AgGridColumnProps | AgGridColumnGroupProps;

interface IProps {
    centerId: number,
    isAdmin: boolean,
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getCenterCLCSDetails: (centerId: number) => Promise<ICenterCLCDetailsResponse>
}

interface IState {
    report: IClcScoresDetails
}

class CenterCLCScoresDetails extends React.Component<IProps, {}> {
    readonly state: IState = {
        report: {
            centerId: 0,
            centerAlias: '',
            eTmps: [],
            hTmps: [],
            chainStarters: [],
            swapFailures: [],
            targetDeclines: [],
            adpEtmp: [],
            adpHtmp: [],
            htmda: [],
            undec: [],
            preRevers: [],
            remoteDon: [],
            pairSched: [],
            chainScheduled: [],
            clcScores: null
        }
    }

    gridApi: GridApi = null;
    gridColumnApi: ColumnApi = null;

    onGridReady = (e: GridReadyEvent) => {
        this.gridApi = e.api;
        this.gridColumnApi = e.columnApi;

        e.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting report data, one moment please...').then(() => {
            this.props.getCenterCLCSDetails(this.props.centerId)
                .then((response: ICenterCLCDetailsResponse) => {
                    this.setState({
                        report: response.data.results
                    });

                    this.props.hideLoading();
                }).catch((ex) => {
                    alert('An error occured while loading report data');
                    this.props.hideLoading();
                });
        });
    }

    onGridSizeChanged = () => {
        // get the current grids width
        let gridWidth = document.getElementById('grid-wrapper').offsetWidth;

        // keep track of which columns to hide/show
        let columnsToShow = [];
        let columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        let totalColsWidth = 0;
        let allColumns = this.gridColumnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];
            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        this.gridColumnApi.setColumnsVisible(columnsToShow, true);
        this.gridColumnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        this.gridApi.sizeColumnsToFit();
    }

    getTotals = (data: IClcScoresDetails): any => {
        let eTmps = _.sumBy(data.eTmps, item => item.clcPoints);
        let hTmps = _.sumBy(data.hTmps, item => item.clcPoints);
        let chainStarters = _.sumBy(data.chainStarters, item => item.clcPoints);
        let swapFailuresPts = _.sumBy(data.swapFailures, item => item.clcPoints);
        let swapFailuresCts = _.sumBy(data.swapFailures, item => item.swapFailureCount);
        let targetDeclines = _.sumBy(data.targetDeclines, item => item.adjustment);
        let adpEtmp = _.sumBy(data.adpEtmp, item => item.clcPoints);
        let adpHtmp = _.sumBy(data.adpHtmp, item => item.clcPoints);
        let htmda = _.sumBy(data.htmda, item => item.adjustment);
        let undec = _.sumBy(data.undec, item => item.adjustment);
        let preRevers = _.sumBy(data.preRevers, item => item.adjustment);
        let remoteDon = _.sumBy(data.remoteDon, item => item.clcPoints);
        let pairSched = _.sumBy(data.pairSched, item => item.totalFclcAdjust);
        let chainScheduled = _.sumBy(data.chainScheduled, item => item.clcPoints);

        return {
            totaleTmps: eTmps,
            totalhTmps: hTmps,
            totalchainStarters: chainStarters,
            totalswapFailuresPts: swapFailuresPts,
            totalswapFailuresCts: swapFailuresCts,
            totaltargetDeclines: targetDeclines,
            totaladpEtmp: adpEtmp,
            totaladpHtmp: adpHtmp,
            totalhtmda: htmda,
            totalundec: undec,
            totalpreRevers: preRevers,
            totalremoteDon: remoteDon,
            totalpairSched: pairSched,
            totalechainScheduled: chainScheduled
        };
    }

    handleDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        this.setState({
            [name]: value
        });
    }

    csvExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            fileName: 'Center-Clc-Scores-Report.csv',
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.headerName == 'R#')
                    return params.node.rowIndex + 1;
                else if (colDef.colId == 'xpDate')
                    return moment(params.value).format('MM/DD/YYYY');

                return params.value;
            }
        };

        this.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        if (this.state.report.clcScores == null)
            return null;

        const totals = this.getTotals(this.state.report);

        const reportChainStartersColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                //valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'right', paddingRight: 60 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 6;
                    }

                    return 1;
                }
            },
            {
                colId: 'donorAlias',
                field: 'donorAlias',
                headerName: 'D Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorType',
                field: 'donorType',
                headerName: 'D Type',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D Type</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorABO',
                field: 'donorABO',
                headerName: 'D ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D ABO</SortableHeaderComponent>,
                width: 75,
                suppressSizeToFit: true,
                sortable: true,
                resizable: true
            },
            {
                colId: 'recipientAlias',
                field: 'recipientAlias',
                headerName: 'R Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'txpDate',
                field: 'txpDate',
                headerName: 'TXP Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>TXP Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'clcPoints',
                field: 'clcPoints',
                headerName: 'CLC Points',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>CLC Points</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }
        ];

        const reportETMpsColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'right', paddingRight: 0 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 13;
                    }

                    return 1;
                }
            },
            {
                colId: 'recipientAlias',
                field: 'recipientAlias',
                headerName: 'R Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'recipientABO',
                field: 'recipientABO',
                headerName: 'R ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R ABO</SortableHeaderComponent>,
                width: 75,
                suppressSizeToFit: true,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'cpra',
                field: 'cpra',
                headerName: 'cPRA',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>cPRA</SortableHeaderComponent>,
                width: 75,
                suppressSizeToFit: true,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'txpDate',
                field: 'txpDate',
                headerName: 'TXP Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>TXP Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'swap',
                field: 'swap',
                headerName: 'Swap',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },

            {
                colId: 'cluster',
                field: 'cluster',
                headerName: 'Cluster',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Cluster</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },

            {
                colId: 'position',
                field: 'position',
                headerName: 'Position',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Position</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },

            {
                colId: 'potentialDonorAlias',
                field: 'potentialDonorAlias',
                headerName: 'PD Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>PD Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },

            {
                colId: 'potentialDonorABO',
                field: 'potentialDonorABO',
                headerName: 'PD ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>PD ABO</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },

            {
                colId: 'donorAlias',
                field: 'donorAlias',
                headerName: 'D Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },

            {
                colId: 'donorABO',
                field: 'donorABO',
                headerName: 'D ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D ABO</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },

            {
                colId: 'compPair',
                field: 'compPair',
                headerName: 'Comp Pair',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Comp Pair</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'clcPoints',
                field: 'clcPoints',
                headerName: 'CLC Points',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>CLC Points</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }
        ];

        const reportHTMpsColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'right', paddingRight: 0 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 12;
                    }

                    return 1;
                }
            },
            {
                colId: 'recipientAlias',
                field: 'recipientAlias',
                headerName: 'R Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'recipientABO',
                field: 'recipientABO',
                headerName: 'R ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R ABO</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'cpra',
                field: 'cpra',
                headerName: 'cPRA',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>cPRA</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'txpDate',
                field: 'txpDate',
                headerName: 'TXP Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>TXP Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'swap',
                field: 'swap',
                headerName: 'Swap',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'cluster',
                field: 'cluster',
                headerName: 'Cluster',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Cluster</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'position',
                field: 'position',
                headerName: 'Position',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Position</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'potentialDonorAlias',
                field: 'potentialDonorAlias',
                headerName: 'PD Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>PD Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'potentialDonorABO',
                field: 'potentialDonorABO',
                headerName: 'PD ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>PD ABO</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorAlias',
                field: 'donorAlias',
                headerName: 'D Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorABO',
                field: 'donorABO',
                headerName: 'D ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D ABO</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'clcPoints',
                field: 'clcPoints',
                headerName: 'CLC Points',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>CLC Points</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }
        ];

        const reportSwapFailureColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'center', paddingLeft: 50 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 2;
                    }

                    return 1;
                }
            },
            {
                colId: 'recipientAlias',
                field: 'recipientAlias',
                headerName: 'R Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'swapFailureCount',
                field: 'swapFailureCount',
                headerName: 'Swap Failure Count',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap Failure Count</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'clcPoints',
                field: 'clcPoints',
                headerName: 'CLC Points',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>CLC Points</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }
        ];

        const reportTargetDeclinesColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'right', paddingRight: 65 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 5;
                    }

                    return 1;
                }
            },
            {
                colId: 'offerDate',
                field: 'offerDate',
                headerName: 'Offer Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Offer Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'recipientAlias',
                field: 'recipientAlias',
                headerName: 'R Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorAlias',
                field: 'donorAlias',
                headerName: 'D Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'decisionDate',
                field: 'decisionDate',
                headerName: 'Decision Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Decision Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'adjustment',
                field: 'adjustment',
                headerName: 'Adjustment',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Adjustment</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }
        ];

        const reportAdpEtmpColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'right', paddingRight: 30 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 7;
                    }

                    return 1;
                }
            },
            {
                colId: 'adpDonor',
                field: 'adpDonor',
                headerName: 'ADP Donor',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>ADP Donor</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'adpRecipient',
                field: 'adpRecipient',
                headerName: 'ADP Recipient',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>ADP Recipient</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'surgeryDate',
                field: 'surgeryDate',
                headerName: 'Surg Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Surg Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorABO',
                field: 'donorABO',
                headerName: 'D ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D ABO</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'recipientABO',
                field: 'recipientABO',
                headerName: 'R ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R ABO</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'pointType',
                field: 'pointType',
                headerName: 'Point Type',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Point Type</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'clcPoints',
                field: 'clcPoints',
                headerName: 'CLC Points',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>CLC Points</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }
        ];

        const reportAdpHtmpColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'right', paddingRight: 35 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 7;
                    }

                    return 1;
                }
            },
            {
                colId: 'adpDonor',
                field: 'adpDonor',
                headerName: 'ADP Donor',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>ADP Donor</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'adpRecipient',
                field: 'adpRecipient',
                headerName: 'ADP Recipient',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>ADP Recipient</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'surgeryDate',
                field: 'surgeryDate',
                headerName: 'Surg Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Surg Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorABO',
                field: 'donorABO',
                headerName: 'D ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D ABO</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'recipientABO',
                field: 'recipientABO',
                headerName: 'R ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R ABO</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'pointType',
                field: 'pointType',
                headerName: 'Point Type',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Point Type</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'clcPoints',
                field: 'clcPoints',
                headerName: 'CLC Points',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>CLC Points</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }
        ];

        const reportHtmdaColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'right', paddingRight: 65 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 5;
                    }

                    return 1;
                }
            },
            {
                colId: 'recipientAlias',
                field: 'recipientAlias',
                headerName: 'R Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorAlias',
                field: 'donorAlias',
                headerName: 'D Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'surgeryDate',
                field: 'surgeryDate',
                headerName: 'XP Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>XP Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'swap',
                field: 'swap',
                headerName: 'Swap',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'adjustment',
                field: 'adjustment',
                headerName: 'Adjustment',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Adjustment</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }
        ];

        const reportUndecColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'right', paddingRight: 55 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 6;
                    }

                    return 1;
                }
            },
            {
                colId: 'offerDate',
                field: 'offerDate',
                headerName: 'Offer Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Offer Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'recipientAlias',
                field: 'recipientAlias',
                headerName: 'R Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorAlias',
                field: 'donorAlias',
                headerName: 'D Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'surgeryDate',
                field: 'surgeryDate',
                headerName: 'Surgery Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Surgery Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'rootCause',
                field: 'rootCause',
                headerName: 'Root Cause',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Root Cause</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'adjustment',
                field: 'adjustment',
                headerName: 'Adjustment',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Adjustment</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }
        ];

        const reportPrereversColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'right', paddingRight: 55 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 6;
                    }

                    return 1;
                }
            },
            {
                colId: 'offerDate',
                field: 'offerDate',
                headerName: 'Offer Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Offer Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'recipientAlias',
                field: 'recipientAlias',
                headerName: 'R Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorAlias',
                field: 'donorAlias',
                headerName: 'D Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'acceptDate',
                field: 'acceptDate',
                headerName: 'Accept Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Accept Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'reversalDate',
                field: 'reversalDate',
                headerName: 'Reversal Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Reversal Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'adjustment',
                field: 'adjustment',
                headerName: 'Adjustment',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Adjustment</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }
        ];

        const reportRemoteDonColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'right', paddingRight: 95 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 4;
                    }

                    return 1;
                }
            },
            {
                colId: 'surgeryDate',
                field: 'surgeryDate',
                headerName: 'TXP Date',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>TXP Date</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorAlias',
                field: 'donorAlias',
                headerName: 'D Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorType',
                field: 'donorType',
                headerName: 'Donor Type',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor Type</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'clcPoints',
                field: 'clcPoints',
                headerName: 'CLC Points',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>CLC Points</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }
        ];

        const reportPairedSchedColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'right', paddingRight: 45 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 11;
                    }

                    return 1;
                }
            },
            {
                colId: 'recipientAlias',
                field: 'recipientAlias',
                headerName: 'R Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'recipientABO',
                field: 'recipientABO',
                headerName: 'R ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                width: 100,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R ABO</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'cPRA',
                field: 'cPRA',
                headerName: 'cPRA',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                width: 100,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>cPRA</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'pdabo',
                field: 'pdabo',
                headerName: 'PD ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                width: 100,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>PD ABO</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorAlias',
                field: 'donorAlias',
                headerName: 'D Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                width: 100,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorABO',
                field: 'donorABO',
                headerName: 'D ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                width: 100,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D ABO</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'pmPc',
                field: 'pmPc',
                headerName: 'PMPc',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                width: 100,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>PMPc</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'compPair',
                field: 'compPair',
                headerName: 'Comp Pair',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                width: 100,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Comp Pair</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'cPRADeduct',
                field: 'cPRADeduct',
                headerName: 'cPRA4 FCLC Deduct',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                width: 210,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>cPRA4 FCLC Deduct</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'btCombo',
                field: 'btCombo',
                headerName: 'BT Combo FCLC Deduct',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                width: 230,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>BT Combo FCLC Deduct</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'totalFclcAdjust',
                field: 'totalFclcAdjust',
                headerName: 'TOTAL FCL Adjust',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                width: 210,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Total FCLC Adjust</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }
        ];

        const reportChainSchedColumns: MyColumn[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 50,
                cellClass: (props: CellClassParams) => props.data.isTotal ? 'center-total-cell' : 'center-list-cell',
                headerClass: 'center-list-header',
                resizable: true,
                suppressSizeToFit: true,
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data.isTotal) {
                        return null;
                    }
                    return props.node.rowIndex + 1;
                },
                cellRendererFramework: (props: ICellRendererParams) => {
                    if (props.data.isTotal) {
                        return <div style={{ textAlign: 'right', paddingRight: 65 }}>Grand Total </div>;
                    }

                    return props.value;
                },
                colSpan: (props: ColSpanParams) => {
                    if (props.data.isTotal) {
                        return 4;
                    }

                    return 1;
                }
            },
            {
                colId: 'donorAlias',
                field: 'donorAlias',
                headerName: 'D Alias',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D Alias</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorType',
                field: 'donorType',
                headerName: 'D Type',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D Type</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'donorABO',
                field: 'donorABO',
                headerName: 'D ABO',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>D ABO</SortableHeaderComponent>,
                width: 300,
                sortable: true,
                // sort: 'asc',
                resizable: true
            },
            {
                colId: 'clcPoints',
                field: 'clcPoints',
                headerName: 'CLC Points',
                cellClass: 'center-list-cell',
                headerClass: 'center-list-header',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>CLC Points</SortableHeaderComponent>,
                sortable: true,
                // sort: 'asc',
                resizable: true
            }

        ];

        const today = new Date();
        const dd = today.getDate();
        const mm = today.getMonth() + 1;
        const yyyy = today.getFullYear();
        const todaydate = mm + '/' + dd + '/' + yyyy;

        return (
            <div className='container'>
                {this.props.isAdmin && (
                    <div className='h3' style={{marginTop: 0, marginBottom: 20}}>CLC Score Details for {this.state.report.centerAlias}</div>
                )}

                <Alert bsStyle='info'>
                    {/* <Row style={{ marginBottom: 10 }}>
                        <Col xs={12} lg={2}>Center</Col>
                        <Col xs={12} lg={3}><strong>{this.state.report.clcScores.alias}</strong></Col>
                    </Row> */}

                    <Row style={{ marginBottom: 10 }}>
                        <Col xs={12} lg={2}>Date</Col>
                        <Col xs={12} lg={3}><strong>{todaydate}</strong></Col>
                    </Row>

                    <Row style={{ marginBottom: 10 }}>
                        <Col xs={12} lg={2}>Current CLC Score</Col>
                        <Col xs={12} lg={3}><strong>{this.state.report.clcScores.clc}</strong></Col>
                    </Row>

                    <Row>
                        <Col xs={12} lg={2}>CLC Point Totals by Type (Detail at bottom)</Col>
                        <Col xs={12} lg={10}>
                            <TableWrapper bordered condensed style={{ marginBottom: 0 }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#ddd' }}>
                                        <th>Chain Starters</th>
                                        <th>Easy to Match Pairs</th>
                                        <th>Hard to Match Pairs</th>
                                        <th>Swap Failures</th>
                                        <th>CLC Declines</th>
                                    </tr>
                                </thead>
                                <tbody style={{ backgroundColor: 'white' }}>
                                    <tr>
                                        <td>{this.state.report.clcScores.ndd}</td>
                                        <td>{this.state.report.clcScores.etmp}</td>
                                        <td>{this.state.report.clcScores.htmp}</td>
                                        <td>{this.state.report.clcScores.matchFailures}</td>
                                        <td>{this.state.report.clcScores.surgDeclines}</td>
                                    </tr>
                                </tbody>
                            </TableWrapper>
                        </Col>
                    </Row>
                </Alert>

                {this.state.report.chainStarters.length > 0 && (
                    <ClcDetailsGrid
                        title='Chain Starters Donated'
                        data={this.state.report.chainStarters}
                        totals={{ rank: 0, txpDate: "Grand Total:", clcPoints: totals.totalchainStarters, isTotal: true }}
                        columns={reportChainStartersColumns}
                    />
                )}

                {this.state.report.eTmps.length > 0 && (
                    <ClcDetailsGrid
                        title='ETMPs Transplanted'
                        data={this.state.report.eTmps}
                        totals={{ rank: 0, compPair: "Grand Total:", clcPoints: totals.totaleTmps, isTotal: true }}
                        columns={reportETMpsColumns}
                    />
                )}

                {this.state.report.adpEtmp.length > 0 && (
                    <ClcDetailsGrid
                        title="ADP ETMP's Transplanted"
                        data={this.state.report.adpEtmp}
                        totals={{ rank: 0, pointType: "Grand Total:", clcPoints: totals.totaladpEtmp, isTotal: true }}
                        columns={reportAdpEtmpColumns}
                    />
                )}

                {this.state.report.hTmps.length > 0 && (
                    <ClcDetailsGrid
                        title="HTMPs Transplanted"
                        data={this.state.report.hTmps}
                        totals={{ rank: 0, donorABO: "Grand Total:", clcPoints: totals.totalhTmps, isTotal: true }}
                        columns={reportHTMpsColumns}
                    />
                )}

                {this.state.report.adpHtmp.length > 0 && (
                    <ClcDetailsGrid
                        title="ADP HTMP's Transplanted"
                        data={this.state.report.adpHtmp}
                        totals={{ rank: 0, pointType: "Grand Total:", clcPoints: totals.totaladpHtmp, isTotal: true }}
                        columns={reportAdpHtmpColumns}
                    />
                )}

                {this.state.report.swapFailures.length > 0 && (
                    <ClcDetailsGrid
                        title="Swap Failure Count"
                        data={this.state.report.swapFailures}
                        totals={{ rank: 0, recipientAlias: "Grand Total:", swapFailureCount: totals.totalswapFailuresCts, clcPoints: totals.totalswapFailuresPts, isTotal: true }}
                        columns={reportSwapFailureColumns}
                    />
                )}

                {this.state.report.htmda.length > 0 && (
                    <ClcDetailsGrid
                        title="Hard to Match Donors Accepted"
                        data={this.state.report.htmda}
                        totals={{ rank: 0, swap: "Grand Total:", adjustment: totals.totalhtmda, isTotal: true }}
                        columns={reportHtmdaColumns}
                    />
                )}

                {this.state.report.undec.length > 0 && (
                    <ClcDetailsGrid
                        title="Unavailable Declines for CLC Targets"
                        data={this.state.report.undec}
                        totals={{ rank: 0, rootCause: "Grand Total:", adjustment: totals.totalundec, isTotal: true }}
                        columns={reportUndecColumns}
                    />
                )}

                {this.state.report.preRevers.length > 0 && (
                    <ClcDetailsGrid
                        title="Preselect Reversals for CLC Targets"
                        data={this.state.report.preRevers}
                        totals={{ rank: 0, reversalDate: "Grand Total:", adjustment: totals.totalpreRevers, isTotal: true }}
                        columns={reportPrereversColumns}
                    />
                )}

                {this.state.report.targetDeclines.length > 0 && (
                    <ClcDetailsGrid
                        title="Declines for CLC Target"
                        data={this.state.report.targetDeclines}
                        totals={{ rank: 0, decisionDate: "Grand Total:", adjustment: totals.totaltargetDeclines, isTotal: true }}
                        columns={reportTargetDeclinesColumns}
                    />
                )}

                {this.state.report.remoteDon.length > 0 && (
                    <ClcDetailsGrid
                        title="Remote Donation Activity"
                        data={this.state.report.remoteDon}
                        totals={{ rank: 0, donorType: "Grand Total:", clcPoints: totals.totalremoteDon, isTotal: true }}
                        columns={reportRemoteDonColumns}
                    />
                )}

                <div style={{ marginBottom: 20, backgroundColor: '#FF9933', padding: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <h2 style={{ margin: 0 }}>CLC SCORE:</h2>
                        <h2 style={{ margin: 0 }}>{this.state.report.clcScores.clc}</h2>
                    </div>
                </div>

                {this.state.report.pairSched.length > 0 && (
                    <ClcDetailsGrid
                        title="Pairs Scheduled for TXP"
                        data={this.state.report.pairSched}
                        totals={{ rank: 0, btCombo: "Grand Total:", totalFclcAdjust: totals.totalpairSched, isTotal: true }}
                        columns={reportPairedSchedColumns}
                    />
                )}

                {this.state.report.chainScheduled.length > 0 && (
                    <ClcDetailsGrid
                        title="Chain Starters Scheduled to Donate"
                        data={this.state.report.chainScheduled}
                        totals={{ rank: 0, donorABO: "FCLC Chain Starter Total:", clcPoints: totals.totalechainScheduled, isTotal: true }}
                        columns={reportChainSchedColumns}
                    />
                )}

                <div style={{ marginBottom: 20, backgroundColor: '#FF9933', padding: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <h2 style={{ margin: 0 }}>TOTAL FCLC ADJUSTMENTS:</h2>
                        <h2 style={{ margin: 0 }}>{totals.totalpairSched + totals.totalechainScheduled}</h2>
                    </div>
                </div>

                <div style={{ backgroundColor: '#FF9933', padding: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <h2 style={{ margin: 0 }}>FINAL FCLC SCORE:</h2>
                        <h2 style={{ margin: 0 }}>{this.state.report.clcScores.clc + totals.totalpairSched + totals.totalechainScheduled}</h2>
                    </div>
                </div>
            </div>
        );
    };
}

export default CenterCLCScoresDetails;